import type { CartItem, RentalDurationUnit } from '@/types/domain';
import type { ID } from '@/types/common';
import { DEFAULT_CURRENCY } from '@/lib/config/constants';

export const sameLine = (item: CartItem, productId: ID) => item.productId === productId;

/**
 * Amount a line should carry in `currency`: the product's configured amount for
 * that currency when present, otherwise the KES list price captured at add time.
 */
export const priceForCurrency = (item: CartItem, currency: string): number => {
  const kes = item.kesPrice ?? item.basePrice ?? item.unitPrice;
  if (!currency || currency === DEFAULT_CURRENCY.code) return kes;
  const alt = item.currencyPrices?.find((c) => c.currency === currency)?.amount;
  return alt ?? kes;
};

export const cartLineKey = (productId: ID) => `${productId}`;

type HireRates = Pick<CartItem, 'dailyPrice' | 'weeklyPrice' | 'monthlyPrice'>;

const PERIOD_ORDER: RentalDurationUnit[] = ['day', 'week', 'month'];

export const hirePriceFor = (item: HireRates, unit: RentalDurationUnit): number | undefined => {
  const price =
    unit === 'day' ? item.dailyPrice : unit === 'week' ? item.weeklyPrice : item.monthlyPrice;
  return price != null && price > 0 ? price : undefined;
};

/** Only hire periods that actually carry a price are selectable. */
export const hirePeriods = (item: HireRates): RentalDurationUnit[] =>
  PERIOD_ORDER.filter((unit) => hirePriceFor(item, unit) !== undefined);

export const hasHireRates = (item: HireRates): boolean => hirePeriods(item).length > 0;

export const defaultHirePeriod = (item: HireRates & { priceSuffix?: string }): RentalDurationUnit | null => {
  const periods = hirePeriods(item);
  if (periods.length === 0) return null;
  const preferred = defaultDurationUnit(item.priceSuffix);
  return periods.includes(preferred) ? preferred : periods[0]!;
};

export const hirePeriodLabel = (unit: RentalDurationUnit): string =>
  unit === 'day' ? 'day' : unit === 'week' ? 'week' : 'month';

export const lineUnitPrice = (item: CartItem): number => {
  if (item.offerType !== 'rent') return item.unitPrice;

  // Products with explicit daily/weekly/monthly rates: unitPrice already holds
  // the rate for the selected period (kept in sync when the period changes).
  if (hasHireRates(item)) return item.unitPrice;

  if (!item.priceSuffix) return item.unitPrice;

  const suffix = item.priceSuffix.toLowerCase();
  const unit = item.durationUnit ?? 'week';

  if (suffix.includes('day')) {
    if (unit === 'day') return item.unitPrice;
    if (unit === 'week') return Math.ceil(item.unitPrice / 7);
    return Math.ceil(item.unitPrice / 30);
  }
  if (suffix.includes('week')) {
    if (unit === 'day') return Math.ceil(item.unitPrice / 7);
    if (unit === 'week') return item.unitPrice;
    return Math.ceil(item.unitPrice * 4);
  }
  if (suffix.includes('month')) {
    if (unit === 'day') return Math.ceil(item.unitPrice / 30);
    if (unit === 'week') return Math.ceil(item.unitPrice / 4);
    return item.unitPrice;
  }

  return item.unitPrice;
};

export const defaultDurationUnit = (priceSuffix?: string): RentalDurationUnit => {
  const suffix = (priceSuffix ?? '').toLowerCase();
  if (suffix.includes('day')) return 'day';
  if (suffix.includes('month')) return 'month';
  return 'week';
};

export const maxUnitsForLine = (
  baseStock: number | undefined,
  factor: number,
): number => {
  if (baseStock == null) return Infinity;
  return Math.max(0, Math.floor(baseStock / Math.max(1, factor)));
};